import { ButcherHeader } from '@components/ButcherHeader';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import { SideNav } from '@components/OrderCreation/OrderSideNav';
import { useEffect, useState } from 'react';
import { OrderBreadcrumb } from '@components/OrderCreation/OrderBreadcrumbs';
import { OrderProfileCard } from '@components/OrderCreation/OrderProfileCard';
import { OrderStepper } from '@components/OrderCreation/OrderStepper';
import {
  CURRENT_CUSTOMIZING_ID,
  errorMessage,
  orderBreadCrumbSteps,
  OrderCreationMasterData,
  orderStepper,
  responseTexts,
} from '@helpers/Constants';
import { Axios } from '@helpers/ApiService';
import { useToast } from '@contexts/Toast';
import { useNavigate } from 'react-router-dom';
import { ApiEndpoints, FrontendRoutes } from '@helpers/Constants/Navigation';
import {
  NewOrderInitiationProps,
  OrderServiceColors,
  OrderServiceProps,
  OrderServices,
} from '@helpers/Models';
import { CustomerOrderService } from '@services/CustomerService';
import { searchService } from '@services/OrderCreationService';
import { UseConfirmationToast } from '@contexts/ConfirmationToast';

export const OrderService: React.FC = () => {
  const { showToastMessage } = useToast();
  const { showConfirmationToast } = UseConfirmationToast();
  const [services, setServices] = useState<OrderServiceProps[]>([]);
  const [colors, setColors] = useState<OrderServiceColors[]>([]);
  const [selectedServiceIds, setSelectedServiceIds] = useState<number[]>([]);
  const [selectedColors, setSelectedColors] = useState<{
    [key: number]: number;
  }>({});
  const navigate = useNavigate();
  const [customerId, setCustomerId] = useState<number>(0);
  const [speciesId, setSpeciesId] = useState<number>();
  const [openColorServiceId, setOpenColorServiceId] = useState<number | null>(
    null,
  );
  const [currentCustomizingId, setCurrentCustomizingId] = useState<string>('');
  const [filteredServices, setFilteredServices] = useState<OrderServiceProps[]>(
    [],
  );
  const [searchKey, setsearchKey] = useState<string>('');
  const [customerDetails, setCustomerDetails] =
    useState<NewOrderInitiationProps>();

  useEffect(() => {
    const customerID = CustomerOrderService.getCustomerId;
    const customerDetails = CustomerOrderService.getCustomerDetails();
    setCustomerDetails(customerDetails);

    if (customerID) {
      setCustomerId(customerID);
      const currentCustomizingId = localStorage.getItem(CURRENT_CUSTOMIZING_ID);
      if (currentCustomizingId) {
        setCurrentCustomizingId(currentCustomizingId);
      }
    } else {
      navigate(FrontendRoutes.CUSTOMER_LIST);
    }
  }, []);

  useEffect(() => {
    if (customerId) {
      getOrderData();
    }
  }, [customerId]);

  useEffect(() => {
    if (speciesId) {
      fetchServiceMaster();
    }
  }, [speciesId]);

  /**
   * Filters the list of order services based on the current search query.
   *
   * This effect runs whenever either the `searchKey` or the `services` list changes.
   * It uses the `searchService` utility function to return only the services
   * whose `name` field includes the query string.
   * The filtered result is cast to `OrderServiceProps[]` and stored in state.
   *
   * @dependency searchKey - The search text input by the user.
   * @dependency services - The complete list of available order services.
   *
   * @sideEffect Updates the `filteredServices` state with the search results.
   */
  useEffect(() => {
    const result = searchService(services, searchKey, 'name');
    setFilteredServices(result);
  }, [searchKey, services]);

  const getOrderData = (): void => {
    const orderData = CustomerOrderService.getParsedOrderData();

    if (orderData && customerId) {
      const customerOrder = orderData[customerId];
      setSpeciesId(customerOrder.speciesId);

      const existingServices =
        customerOrder?.species?.[currentCustomizingId]?.services;

      if (existingServices && Array.isArray(existingServices)) {
        const serviceIds = existingServices.map((s) => s.serviceId);
        const colors: Record<number, number> = {};

        existingServices.forEach((s: OrderServices) => {
          if (s.color !== undefined) {
            colors[s.serviceId] = Number(s.color);
          }
        });

        setSelectedServiceIds(serviceIds);
        setSelectedColors(colors);
      }
    }
  };

  const fetchServiceMaster = async (): Promise<void> => {
    try {
      const response = await Axios.post(ApiEndpoints.FETCH_SPECIES_MASTERDATA, {
        speciesId: speciesId,
        masterDataType: OrderCreationMasterData.SERVICE_MASTER_DATA,
      });
      setServices(response.data.data.masterData.services);
      setFilteredServices(response.data.data.masterData.services);
      setColors(response.data.data.masterData.colors);
    } catch (error) {
      showToastMessage(errorMessage.FETCH_ERROR, 'error');
    }
  };

  const handleColorSelect = (serviceId: number, colorId: number) => {
    setSelectedColors((prevSelectedColors) => ({
      ...prevSelectedColors,
      [serviceId]: colorId,
    }));
    setOpenColorServiceId(null);
  };

  const toggleServiceSelection = (serviceId: number) => {
    // Check if the serviceId is already selected
    setSelectedServiceIds((prevSelected) => {
      if (prevSelected.includes(serviceId)) {
        return prevSelected.filter((id) => id !== serviceId);
      } else {
        return [...prevSelected, serviceId];
      }
    });
  };

  const handleNext = (): void => {
    if (selectedServiceIds.length === 0) {
      showConfirmationToast(
        responseTexts.ORDER_SERVICE_SELECTION_WARNING,
        'No Service Selected',
        'info',
        handleSave, // Function call to save the service array as empty
        'Confirm',
        undefined,
        'Cancel',
      );
      return;
    } else {
      handleSave();
    }
  };

  const handleSave = (): void => {
    const orderDataPayload = CustomerOrderService.getParsedOrderData();

    if (customerId) {
      const updatedServices = [];

      // Service color validation
      for (const serviceId of selectedServiceIds) {
        const serviceData: { serviceId: number; color?: number } = {
          serviceId,
        };
        const serviceInfo = filteredServices.find((s) => s.id === serviceId);

        const selectedColor = selectedColors[serviceId];

        // If service requires color but none selected, show error message
        if (
          serviceInfo?.colors === 1 &&
          (selectedColor === undefined || selectedColor === null)
        ) {
          showToastMessage(errorMessage.SERVICE_COLOR_REQUIRED, 'error');
          return;
        }
        // Get the selected color id
        if (selectedColor !== undefined) {
          serviceData.color = Number(selectedColor);
        }
        updatedServices.push(serviceData);
      }

      const existingSpecies =
        orderDataPayload[customerId].species[currentCustomizingId] ?? {};
      const existingServices = existingSpecies.services ?? [];

      // To keep the deposit amount if there's any for the selected service
      const modifiedService = updatedServices ?? [];

      // Only keep services that are still in updated
      const finalServices = existingServices.filter(
        (service: { serviceId: number }) =>
          modifiedService.some(
            (modifiedService) =>
              modifiedService.serviceId === service.serviceId,
          ),
      );

      // Merge with updated (add new or update existing)
      modifiedService.forEach((newService) => {
        const index = finalServices.findIndex(
          (service: { serviceId: number }) =>
            service.serviceId === newService.serviceId,
        );
        if (index > -1) {
          // Merge properties
          finalServices[index] = {
            ...finalServices[index],
            ...newService,
          };
        } else {
          // Add if not present
          finalServices.push(newService);
        }
      });

      // Update the service with the order data
      orderDataPayload[customerId].species[currentCustomizingId] = {
        ...existingSpecies,
        services: finalServices,
      };
      showToastMessage(responseTexts.SERVICE_CREATE_SUCCESS, 'success');

      localStorage.setItem('orderData', JSON.stringify(orderDataPayload));
      navigate(FrontendRoutes.CREATE_ORDER_PRODUCT);
    }
  };

  return (
    <div className="flex h-screen w-screen">
      {/* Sidebar */}
      <SideNav active={2} />
      {/* Main Content */}
      <div className="flex-1 overflow-auto relative">
        <div>
          {/* Header */}
          <ButcherHeader />
          {/* Breadcrumbs */}
          <OrderBreadcrumb
            steps={orderBreadCrumbSteps}
            customerName={`${customerDetails?.firstName} ${customerDetails?.lastName}`}
            activeItem="New Order"
            customerId={customerId}
          />
        </div>
        <div className="p-3 pb-20">
          <OrderProfileCard />
          {/* Stepper */}
          <OrderStepper steps={orderStepper} currentStep={2} />

          <form className="space-y-8">
            <div>
              <div className="flex justify-between mt-4">
                <h2 className="text-lg font-medium mb-2">Service</h2>
                <div className="relative">
                  <input
                    type="text"
                    value={searchKey}
                    onChange={(e) => setsearchKey(e.target.value)}
                    placeholder="Search..."
                    className="border p-2 pl-3 pr-10 w-80 accent-primary focus:outline-primary border-gray-300 rounded"
                  />
                  <FontAwesomeIcon
                    icon={faSearch}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500"
                  />
                </div>
              </div>
              {/* The following design was removed from figma */}
              {/* <div className="mb-3">
                <span className="text-gray-600 text-sm">
                  Please allocate the service to the species selected
                </span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 mb-3 gap-4">
                <div className="bg-quaternary p-1 rounded-sm">
                  <div className="flex justify-between text-sm">
                    <div className="flex items-center space-x-3 bg-quaternary">
                      <span className="text-gray-700">Species:</span>
                      <div className="flex items-center font-semibold">
                        {speciesName}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-quaternary p-1 rounded-sm">
                  <div className="flex justify-between text-sm">
                    <div className="flex items-center space-x-3 bg-quaternary">
                      <span className="text-gray-700">Quantity:</span>
                      <div className="flex items-center font-semibold">
                        {quantity}
                      </div>
                    </div>
                  </div>
                </div>
              </div> */}
              <div className="space-y-2 mt-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {filteredServices.map((service) => (
                    <div
                      key={service.id}
                      className={`p-4 rounded-lg shadow-md transition-colors ${selectedServiceIds.includes(service.id) ? 'bg-tertiary border border-primary' : 'bg-[#FFFEFA]'} h-[136px]`}
                      onClick={() => toggleServiceSelection(service.id)}
                    >
                      <div className="flex justify-between font-bold text-sm text-gray-700">
                        <div>{service.name}</div>
                        <div>${service.rate}</div>
                      </div>
                      <span className="text-sm text-[#555F71] mb-3 font-semibold">
                        {service.feeType}
                      </span>

                      {/* Check if service has color option (colors === 1) */}
                      {service.colors === 1 && (
                        <div className="relative">
                          <button
                            type="button"
                            className="mt-3 w-full p-2 border border-gray-300 bg-white rounded flex justify-between items-center cursor-pointer overflow-hidden"
                            onClick={() => {
                              setOpenColorServiceId(
                                openColorServiceId === service.id
                                  ? null
                                  : service.id,
                              );
                            }}
                          >
                            <span className="truncate max-w-[100%] overflow-hidden">
                              {selectedColors[service.id]
                                ? colors.find(
                                    (c) => c.id === selectedColors[service.id],
                                  )?.color
                                : 'Select a Color'}
                            </span>
                            <span>
                              <svg
                                width="15"
                                height="8"
                                viewBox="0 0 15 8"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M2 1.5L7.5 6.5L13 1.5"
                                  stroke="#A09CAB"
                                  strokeWidth="2.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </span>
                          </button>

                          {openColorServiceId === service.id && (
                            <div className="absolute left-0 right-0 mt-1 bg-white border border-gray-300 rounded shadow-md z-10">
                              <button type="button" className="w-full h-full" />
                              <div className="flex justify-between items-center p-2 border-b">
                                <span className="font-semibold">
                                  Choose Color
                                </span>
                                <button
                                  type="button"
                                  onClick={() => setOpenColorServiceId(null)}
                                  className="text-gray-500"
                                >
                                  ✕
                                </button>
                              </div>
                              <div className="overflow-y-auto max-h-80">
                                {colors.length > 0 ? (
                                  colors.map((color) => (
                                    <button
                                      key={color.id}
                                      type="button"
                                      className="w-full p-2 text-left cursor-pointer border border-transparent hover:bg-tertiary hover:border-primary"
                                      onClick={() =>
                                        handleColorSelect(service.id, color.id)
                                      }
                                    >
                                      {color.color}
                                    </button>
                                  ))
                                ) : (
                                  <div className="p-2 text-gray-500">
                                    No colors available
                                  </div>
                                )}
                              </div>
                              <div className="flex justify-end border-t p-2 space-x-2">
                                <button
                                  type="button"
                                  className="mr-3 text-primary-500"
                                  onClick={() => setOpenColorServiceId(null)}
                                >
                                  Cancel
                                </button>
                                <button
                                  type="button"
                                  className="bg-primary text-white px-3 py-1 rounded"
                                >
                                  Save
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </form>
        </div>
        <div className="flex justify-between gap-4 fixed bottom-0 left-14 w-[calc(100%-64px)] p-4">
          <button
            onClick={() => navigate(FrontendRoutes.CREATE_ORDER_CHECKIN)}
            type="button"
            className="w-[50%] px-6 py-2 border border-primary rounded-sm text-sm font-medium text-primary bg-white hover:bg-primary hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
          >
            Back
          </button>
          <button
            onClick={handleNext}
            type="submit"
            className="w-[50%] px-6 py-2 border border-transparent rounded-sm text-sm font-medium text-white bg-primary hover:bg-dark-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary flex items-center justify-center space-x-2"
          >
            <span>Next</span>
          </button>
        </div>
      </div>
    </div>
  );
};
